import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {useSearchParams} from 'react-router-dom';
import {useAppSelector} from 'store/customer';
import {getisRoomPlannerEditing} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useOpenPlaceholderEditor} from 'components/customer/RoomPlanner/components/FloatingMenu/Actions/PlaceholderEdit';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {getPlaceholderSelectionInfo} from 'components/customer/RoomPlanner/helpers/placeholder/selectionInfo';
import {ButtonContainer} from 'components/customer/RoomPlanner/components/Menu/Header';
import {CBCButton, Icon} from 'shared/helpers';
import {
    selectPageRestricted,
    selectRestrictionMessageDismissed,
} from 'store/customer/appSlice';
import {mobileLandscapeAndPortraitDevices} from 'shared/Breakpoints';
import {PLACEHOLDER_NOT_IN_ORDER_LABEL} from 'components/customer/RoomPlanner/helpers/placeholder/copy';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';

/**
 * Floating info card for selected appliance placeholders.
 * Specs only from the mesh — no product Selections/details APIs, no 3D preview.
 * @return {JSX.Element | null}
 */
export const PlaceholderDropdown = () => {
    const {selectedCabinetGroup, setSelectedCabinetGroup, placeholderListRef} =
        useRoomPlannerContext();
    const [, setSearchParams] = useSearchParams();
    const isRoomPlannerEditing = useAppSelector(getisRoomPlannerEditing);
    const isRestricted = useAppSelector(selectPageRestricted);
    const messageDismissed = useAppSelector(selectRestrictionMessageDismissed);
    const handleEdit = useOpenPlaceholderEditor();
    const isRoomPreview = useRoomPreview();

    const isPlaceholder = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );

    const handleCancel = useCallback(() => {
        setSearchParams({});
        setSelectedCabinetGroup?.(null);
    }, [setSearchParams, setSelectedCabinetGroup]);

    const {name, dimensions, quantity} = useMemo(
        () =>
            getPlaceholderSelectionInfo(
                selectedCabinetGroup,
                placeholderListRef?.current
            ),
        [selectedCabinetGroup, placeholderListRef]
    );

    if (!isPlaceholder || !selectedCabinetGroup || isRoomPlannerEditing) {
        return null;
    }

    return (
        <DropdownContainer
            $hasTopOffset={(isRestricted && !messageDismissed) || false}>
            <RowOne>
                <ProductNumberAndName>{name}</ProductNumberAndName>
                <CloseControl>
                    <ButtonContainer onClick={handleCancel}>
                        <Icon iconName="Button-Cancel.svg" />
                    </ButtonContainer>
                </CloseControl>
            </RowOne>

            <RowThree>
                <OrderNotice>{PLACEHOLDER_NOT_IN_ORDER_LABEL}</OrderNotice>
                <ProductDimensions>
                    <Quantity>
                        <Header>Specs:</Header>
                        <Title>Quantity</Title>
                        <Text>{quantity}</Text>
                    </Quantity>
                    <Dimensions>
                        <Dimension>
                            <Title>Height</Title>
                            <Text>{dimensions.height || '—'}</Text>
                        </Dimension>
                        <Dimension>
                            <Title>Width</Title>
                            <Text>{dimensions.width || '—'}</Text>
                        </Dimension>
                        <Dimension>
                            <Title>Depth</Title>
                            <Text>{dimensions.depth || '—'}</Text>
                        </Dimension>
                    </Dimensions>
                </ProductDimensions>
            </RowThree>

            {isRoomPreview ? null : (
                <ButtonRow>
                    <Button
                        onClick={handleCancel}
                        iconName="Button-Cancel.svg"
                        className="job-button button-light"
                        style={{color: '#8A8080', flex: 1}}>
                        Cancel
                    </Button>
                    <CBCButton
                        id="placeholder-edit-button"
                        type="button"
                        onClick={handleEdit}
                        iconName="Button-Tick.svg"
                        className="job-button button-blue"
                        style={{flex: 1}}>
                        Edit
                    </CBCButton>
                </ButtonRow>
            )}
        </DropdownContainer>
    );
};

const DropdownContainer = styled.div<{$hasTopOffset: boolean}>`
    background: #ffffff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    color: #000000;
    pointer-events: auto;
    margin-top: 0;
    position: absolute;
    top: 48px;
    left: 5px;
    padding: 10px;
    box-shadow: 2px 2px 10px -1px #bdbdbd;
    width: 400px;
    max-height: calc(100% - 56px);
    overflow: auto;

    ${mobileLandscapeAndPortraitDevices`
        position: initial;
        width: initial;
        max-height: none;
        overflow: visible;
        box-shadow: none;
        padding: 0 10px;
        margin-top: 0;
        background: transparent;
    `}

    ${({$hasTopOffset}) => {
        if ($hasTopOffset) {
            return `
                top: 103px;
            `;
        }
    }};
`;

const RowOne = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    color: ${({theme}) => theme.colors.secondary.main};
    font-size: 16px;
    font-weight: 600;
    min-width: 0;
    border-bottom: 1px solid ${({theme}) => theme.colors.secondary.main};
    padding-bottom: 8px;
`;

const CloseControl = styled.div`
    flex-shrink: 0;

    ${mobileLandscapeAndPortraitDevices`
        display: none;
    `}
`;

const ProductNumberAndName = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const OrderNotice = styled.div`
    margin-bottom: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b6f70;
    line-height: 1.3;
`;

const RowThree = styled.div`
    padding: 12px 0;
    display: flex;
    flex-direction: column;
`;

const ProductDimensions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
`;

const Quantity = styled.div`
    display: flex;
    flex-direction: column;
`;

const Dimensions = styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
`;

const Dimension = styled.div`
    display: flex;
    flex-direction: column;
`;

const Text = styled.div`
    text-transform: uppercase;
    font-size: 0.8em;
    color: black;
    line-height: 14px;
    font-weight: 500;
    white-space: nowrap;
`;

const Header = styled(Text)`
    color: ${({theme}) => theme.colors.secondary?.main};
    font-weight: 800;
`;

const Title = styled(Text)`
    color: gray;
    font-weight: bold;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 8px;
    width: 100%;

    ${mobileLandscapeAndPortraitDevices`
        display: none;
    `}
`;

const Button = styled((props: React.ComponentProps<typeof CBCButton>) => (
    <CBCButton {...props} />
))`
    border-radius: 40px !important;
    padding: 5px 10px 5px 5px !important;
    width: fit-content !important;
    font-size: 0.95em !important;
    &:hover {
        border: 1px solid white;
    }
`;
